/**
 * Festival mode on this device (plan.md step 59).
 *
 * On by itself in the MyRave app (its user agent) and in the web app
 * installed to a home screen; a desktop browser can switch it on to try it
 * (`myrave:platform:festivalMode`). Everything here stays on the device:
 * which rave was opened last, whether to use location, and the location
 * itself — which is read only when asked and never leaves this file's caller.
 */
import { isFestivalModeClient, type FestivalCandidate } from '@myrave/shared';
import { readJson, writeJson } from './storage';
import { db } from '@/offline/db';
import { ownStored } from '@/offline/bundle';
import type { MyFestival } from './api';
import { signedInHere } from './signOut';

export function inFestivalMode(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  return isFestivalModeClient(navigator.userAgent, standalone) || readJson<boolean>(null, 'festivalMode', false);
}

/** Signed out nothing is kept and nothing kept is read (the user, 2026-10-27: online only). */
export function rememberLastRave(editionId: string): void {
  if (signedInHere()) writeJson(null, 'lastRave', editionId);
}

export function lastRave(): string | null {
  return signedInHere() ? readJson<string | null>(null, 'lastRave', null) : null;
}

/** Has this person said location may be used to find the festival they are at? Off unless they did. */
export function festivalLocationOn(): boolean {
  return readJson<boolean>(null, 'festivalLocation', false);
}

export function setFestivalLocation(on: boolean): void {
  writeJson(null, 'festivalLocation', on);
}

/** Low-power, recent-is-fine, and a refusal is simply "no place". */
export function currentPlace(): Promise<{ lat: number; lng: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, maximumAge: 10 * 60_000, timeout: 10_000 },
    );
  });
}

/** Every rave this device has a tie to: attended, stored for offline, or opened last. */
export async function festivalCandidates(attending: readonly MyFestival[]): Promise<FestivalCandidate[]> {
  // Only this account's copies: another account's rave is not its tie (2026-10-16).
  const stored = (await db.bundles.toArray().catch(() => [])).filter((s) => ownStored(s));
  const last = lastRave();
  const byId = new Map<string, FestivalCandidate>();
  for (const s of stored) {
    const b = s.bundle;
    byId.set(s.editionId, {
      editionId: s.editionId, subdomain: b.festival.subdomain, editionSlug: b.edition.slug, name: b.edition.name,
      gatesOpenAt: b.edition.gatesOpenAt ? new Date(b.edition.gatesOpenAt) : null,
      endsAt: b.edition.endsAt ? new Date(b.edition.endsAt) : null,
      lat: b.edition.lat ?? null, lng: b.edition.lng ?? null,
      attending: false, stored: true, lastOpened: s.editionId === last,
    });
  }
  for (const f of attending) {
    const known = byId.get(f.editionId);
    byId.set(f.editionId, {
      ...(known ?? {
        editionId: f.editionId, subdomain: f.subdomain, editionSlug: f.editionSlug, name: f.editionName,
        gatesOpenAt: f.gatesOpenAt ? new Date(f.gatesOpenAt) : null, endsAt: f.endsAt ? new Date(f.endsAt) : null,
        lat: null, lng: null, stored: false, lastOpened: f.editionId === last,
      }),
      attending: true,
    });
  }
  return [...byId.values()];
}
